import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsers } from '../../store/slices/usersSlice';
import apiService from '../../services/api';
import { subscribeToRefundsUpdates } from '../../services/socket';
import { Eye, Download, X, Copy, RotateCcw, Loader2, Printer } from 'lucide-react';
import { exportRefundsToExcel } from '../../utils/excelExport';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLatinKeyboardGroupProps } from '../../hooks/useLatinKeyboardGroupProps';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTime, formatCurrency } from '../../utils/format';
import { getTokens } from '../../utils/tokens';
import { printRefundReceipt, storeInfoForSessionPrint } from '../pos/Receipt';
import {
  PageHeader,
  StatStrip,
  GhostBtn,
  PrimaryBtn,
  Toolbar,
  FilterChip,
  StatusPill,
  DataTable,
  Pagination,
  MonoMoney,
  AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const Refunds = () => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.theme);
  const { list: users } = useSelector((state) => state.users);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const { containerProps: refundsToolbarReceiptSearchProps } = useLatinKeyboardGroupProps();

  const tk = getTokens(mode);

  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [receiptCopied, setReceiptCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [printingRefundId, setPrintingRefundId] = useState(null);

  useEffect(() => {
    setReceiptCopied(false);
  }, [selectedRefund]);

  const [filters, setFilters] = useState({
    session_number: '',
    cashier_id: 'all',
    approved_by: 'all',
    order_status: 'all',
    receipt_num: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    dispatch(fetchUsers(true));
  }, [dispatch]);

  const fetchRefundsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getRefunds({ limit: 500, ...filters });
      setRefunds(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('alerts.failedToLoadRefunds'));
      console.error('Failed to fetch refunds', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRefundsData(); }, [filters]);

  useEffect(() => {
    const unsubscribe = subscribeToRefundsUpdates(fetchRefundsData);
    return unsubscribe;
  }, [filters]);

  useEffect(() => {
    return () => setSelectedRefund(null);
  }, []);

  const clearFilters = () => {
    setFilters({
      session_number: '',
      cashier_id: 'all',
      approved_by: 'all',
      order_status: 'all',
      receipt_num: '',
      date_from: '',
      date_to: '',
    });
  };

  const hasActiveFilters =
    filters.session_number.trim() !== '' ||
    filters.cashier_id !== 'all' ||
    filters.approved_by !== 'all' ||
    filters.order_status !== 'all' ||
    filters.receipt_num.trim() !== '' ||
    filters.date_from !== '' ||
    filters.date_to !== '';

  const handleViewRefund = async (refund) => {
    try {
      const res = await apiService.getRefund(refund.id);
      setSelectedRefund(res.data);
    } catch (err) {
      showToast(t('alerts.failedToLoadRefund'), "warning");
      console.error('Failed to fetch refund details', err);
    }
  };

  const handlePrintRefund = async (refund) => {
    if (!refund) return;
    setPrintingRefundId(refund.id);
    try {
      let fullRefund = refund;
      if (!Array.isArray(refund.items) || refund.items.length === 0) {
        const res = await apiService.getRefund(refund.id);
        fullRefund = res?.data || refund;
      }
      const storeRes = await apiService.getStore();
      const storeInfo = storeInfoForSessionPrint(storeRes?.data || storeRes || {});
      const items = (fullRefund.items || []).map((it) => ({
        product_name: it.product_name || it.item_name || it.name || '',
        refunded_quantity: it.refunded_quantity,
        refunded_amount: it.refunded_amount,
        product_unit_type: it.product_unit_type || it.unit_type,
      }));
      await printRefundReceipt(
        {
          receipt_num: fullRefund.receipt_num || '',
          cashier_name: fullRefund.refund_cashier_name || t('refunds.refundCashier'),
          refunded_at: fullRefund.created_at || new Date().toISOString(),
          items,
          total: fullRefund.amount,
        },
        storeInfo
      );
    } catch (err) {
      showToast(err?.body?.error || err?.message || t('alerts.failedToPrintReceipt'), 'error');
      console.error('Failed to print refund receipt', err);
    } finally {
      setPrintingRefundId(null);
    }
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];

  const todayCount = refunds.filter(
    (r) => (r.created_at || '').split('T')[0] === today
  ).length;
  const todayAmount = refunds
    .filter((r) => (r.created_at || '').split('T')[0] === today)
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
  const monthCount = refunds.filter((r) => {
    if (!r.created_at) return false;
    const d = new Date(r.created_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const monthAmount = refunds
    .filter((r) => {
      if (!r.created_at) return false;
      const d = new Date(r.created_at);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);

  const totalAmount = refunds.reduce(
    (sum, r) => sum + parseFloat(r.amount || 0), 0
  );
  const totalRefundedCount = refunds.length;

  // ── Search + pagination ────────────────────────────────────────────────────
  const filteredRefunds = React.useMemo(() => {
    let list = refunds;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          (r.receipt_num || '').toLowerCase().includes(q) ||
          (r.order_cashier_name || '').toLowerCase().includes(q) ||
          (r.refund_cashier_name || '').toLowerCase().includes(q) ||
          (r.approver_name || '').toLowerCase().includes(q)
      );
    }
    if (filters.date_from) {
      const from = new Date(filters.date_from);
      list = list.filter((r) => r.created_at && new Date(r.created_at) >= from);
    }
    if (filters.date_to) {
      const to = new Date(filters.date_to);
      list = list.filter((r) => r.created_at && new Date(r.created_at) <= to);
    }
    return list;
  }, [refunds, searchQuery, filters.date_from, filters.date_to]);

  const totalPages = Math.ceil(filteredRefunds.length / itemsPerPage) || 1;

  const currentRefunds = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRefunds.slice(start, start + itemsPerPage);
  }, [filteredRefunds, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [filters, searchQuery]);

  const cashiers = (users || []).filter((u) => u.role === 'cashier');
  const approvers = (users || []).filter((u) => ['manager', 'admin', 'owner'].includes(u.role));

  // ── Status helpers ─────────────────────────────────────────────────────────
  const getRefundStatusKind = (status) => {
    if (!status) return 'closed';
    if (status === 'refunded') return 'approved';
    if (status === 'partially_refunded') return 'pending';
    if (status === 'rejected') return 'rejected';
    return 'pending';
  };

  const getRefundStatusLabel = (status) => {
    if (status === 'refunded') return t('reports.refunded');
    if (status === 'partially_refunded') return t('refunds.partialRefund') || 'Partial';
    if (status === 'rejected') return t('refunds.statusRejected');
    if (status === 'pending') return t('refunds.statusPending');
    if (status === 'completed') return t('reports.completed');
    return status || '—';
  };

  const handleExport = async () => {
    if (!filteredRefunds.length) return;
    setExporting(true);
    try {
      const labels = t('refunds.excelExport');
      const L = typeof labels === 'object' && labels !== null ? labels : {};
      const today = new Date().toISOString().split('T')[0];
      await exportRefundsToExcel(filteredRefunds, `refunds-${today}.xlsx`, {
        ...L,
        formatRefundOrderStatus: getRefundStatusLabel,
      });
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  // ── Filter select options ──────────────────────────────────────────────────
  const cashierOptions = [
    { value: 'all', label: t('common.all') },
    ...cashiers.map((c) => ({ value: String(c.id), label: c.full_name })),
  ];

  const approverOptions = [
    { value: 'all', label: t('common.all') },
    ...approvers.map((a) => ({ value: String(a.id), label: a.full_name })),
  ];

  const statusOptions = [
    { value: 'all', label: t('common.all') },
    { value: 'refunded', label: t('reports.refunded') },
    { value: 'partially_refunded', label: t('refunds.partialRefund') || 'Partial' },
  ];

  const refundsFilterLabelStyle = {
    fontSize: 10.5,
    color: tk.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  const REFUNDS_FILTER_COL_PX = 150;
  const REFUNDS_STATUS_FILTER_COL_PX = 128;

  const refundsFilterColStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: `0 0 ${REFUNDS_FILTER_COL_PX}px`,
    width: REFUNDS_FILTER_COL_PX,
    maxWidth: REFUNDS_FILTER_COL_PX,
    minWidth: 0,
  };

  // datetime-local needs more room than a bare date input.
  const REFUNDS_DATE_COL_PX = 175;
  const refundsDateColStyle = {
    ...refundsFilterColStyle,
    flex: `0 0 ${REFUNDS_DATE_COL_PX}px`,
    width: REFUNDS_DATE_COL_PX,
    maxWidth: REFUNDS_DATE_COL_PX,
  };

  const refundsStatusFilterColStyle = {
    ...refundsFilterColStyle,
    flex: `0 0 ${REFUNDS_STATUS_FILTER_COL_PX}px`,
    width: REFUNDS_STATUS_FILTER_COL_PX,
    maxWidth: REFUNDS_STATUS_FILTER_COL_PX,
  };

  const refundsDateInputStyle = {
    height: 34,
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 10px',
    borderRadius: 8,
    background: tk.surface,
    border: `1px solid ${tk.border}`,
    color: tk.text,
    fontFamily: 'inherit',
    fontSize: 12,
    outline: 'none',
    cursor: 'pointer',
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const cols = [
    { key: 'order_id', label: t('reports.orderNo') },
    { key: 'datetime', label: t('common.date') },
    { key: 'cashier', label: t('refunds.refundCashier') },
    { key: 'amount', label: t('common.amount') },
    { key: 'status', label: t('common.status') },
    { key: 'actions', label: '' },
  ];

  // ── Style helpers ──────────────────────────────────────────────────────────
  const fontFamily = language === 'ar' ? '"Cairo", sans-serif' : '"DM Sans", sans-serif';

  const pageStyle = {
    background: tk.bg,
    color: tk.text,
    padding: 20,
    minHeight: '100%',
    fontFamily,
    overflowY: 'auto',
  };

  const actionIconBtnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6,
    background: tk.sunken, border: `1px solid ${tk.border}`,
    color: tk.textSecondary, cursor: 'pointer',
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };

  const modalBoxStyle = {
    background: tk.surface, border: `1px solid ${tk.border}`,
    borderRadius: 14, maxWidth: 640, width: '100%', margin: '0 16px',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: tk.shadowLg,
  };

  const cancelBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0 16px', height: 36,
    background: 'transparent', border: `1px solid ${tk.border}`,
    color: tk.textSecondary, borderRadius: 8, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
  };

  const renderCell = (row, key) => {
    switch (key) {
      case 'order_id':
        return (
          <span style={{
            fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
            fontSize: 12, color: tk.accentText,
          }}>
            {row.receipt_num || `#${row.order_id || '—'}`}
          </span>
        );
      case 'datetime':
        return (
          <span style={{ color: tk.textSecondary, fontSize: 12 }}>
            {formatDateTime(row.created_at)}
          </span>
        );
      case 'cashier':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <AvatarInitials name={row.refund_cashier_name || '?'} tk={tk} size={24} />
            <span style={{
              color: tk.text, fontSize: 12.5,
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {row.refund_cashier_name || '—'}
            </span>
          </div>
        );
      case 'amount':
        return (
          <MonoMoney
            amount={row.amount}
            tk={tk}
            size={12.5}
            weight={700}
            color={tk.dangerText}
          />
        );
      case 'status':
        return (
          <StatusPill tk={tk} kind={getRefundStatusKind(row.order_status)}>
            {getRefundStatusLabel(row.order_status)}
          </StatusPill>
        );
      case 'actions':
        return (
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button
              style={actionIconBtnStyle}
              onClick={() => handleViewRefund(row)}
              title={t('common.view')}
            >
              <Eye size={14} />
            </button>
            <button
              style={{
                ...actionIconBtnStyle,
                opacity: printingRefundId === row.id ? 0.5 : 1,
                cursor: printingRefundId === row.id ? 'not-allowed' : 'pointer',
              }}
              onClick={() => handlePrintRefund(row)}
              disabled={printingRefundId === row.id}
              title={t('common.print')}
            >
              {printingRefundId === row.id ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle} dir={isRTL ? 'rtl' : 'ltr'} className={`theme-${mode}`}>

      <PageHeader
        tk={tk}
        title={t('refunds.refunds') || 'Refunds'}
        subtitle={language === 'ar' ? 'عمليات الإلغاء والإرجاع والمدفوعات المستردة' : 'Voids, returns & refunded payments'}
        actions={
          <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={handleExport}>
            {exporting ? t('monthlyClosing.downloading') : t('refunds.downloadExcel')}
          </GhostBtn>
        }
      />

      {loading ? (
        <StatStripSkeleton tk={tk} count={5} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: t('common.today') || 'Today', value: String(todayCount) },
            { label: t('refunds.todayTotalLabel') || `${t('common.today') || 'Today'} ${t('common.total') || 'Total'}`, value: todayAmount, money: true },
            { label: t('common.thisMonth') || 'Month', value: String(monthCount) },
            { label: t('refunds.monthTotalLabel') || `${t('common.thisMonth') || 'Month'} ${t('common.total') || 'Total'}`, value: monthAmount, money: true },
            {
              label: t('refunds.totalRefundedAmount') || `Total refunded (${t('currency.egp') || 'EGP'})`,
              value: totalAmount,
              money: true,
              delta: `${totalRefundedCount} ${t('refunds.refunds') || 'Refunds'}`,
              positive: true,
            },
          ]}
        />
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('refunds.searchByReceiptNo') || 'Search refunds...'}
        searchValue={searchQuery}
        onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
        searchWrapProps={refundsToolbarReceiptSearchProps}
        searchInputProps={{
          name: 'refundsListReceiptSearch',
          inputMode: 'text',
          dir: isRTL ? 'rtl' : 'ltr',
          lang: language,
          autoComplete: 'off',
          autoCapitalize: 'none',
          autoCorrect: 'off',
          spellCheck: false,
        }}
        filters={
          <>
            <div style={refundsFilterColStyle}>
              <span style={refundsFilterLabelStyle}>{t('sessions.sessionNo')}</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t('sessions.searchBySessionNo')}
                value={filters.session_number}
                onChange={(e) => { setFilters((f) => ({ ...f, session_number: e.target.value.replace(/\D/g, '') })); setCurrentPage(1); }}
                style={refundsDateInputStyle}
              />
            </div>
            <div style={refundsStatusFilterColStyle}>
              <span style={refundsFilterLabelStyle}>{t('common.status')}</span>
              <FilterChip
                tk={tk}
                label={t('common.status')}
                value={filters.order_status}
                onChange={(v) => setFilters((f) => ({ ...f, order_status: v }))}
                options={statusOptions}
                active={filters.order_status !== 'all'}
              />
            </div>
            <div style={refundsFilterColStyle}>
              <span style={refundsFilterLabelStyle}>{t('refunds.refundCashier')}</span>
              <FilterChip
                tk={tk}
                label={t('refunds.refundCashier')}
                value={filters.cashier_id}
                onChange={(v) => setFilters((f) => ({ ...f, cashier_id: v }))}
                options={cashierOptions}
                active={filters.cashier_id !== 'all'}
              />
            </div>
            <div style={refundsFilterColStyle}>
              <span style={refundsFilterLabelStyle}>{t('refunds.approvedBy')}</span>
              <FilterChip
                tk={tk}
                label={t('refunds.approvedBy')}
                value={filters.approved_by}
                onChange={(v) => setFilters((f) => ({ ...f, approved_by: v }))}
                options={approverOptions}
                active={filters.approved_by !== 'all'}
              />
            </div>
            <div style={refundsDateColStyle}>
              <span style={refundsFilterLabelStyle}>{t('common.from') || 'From'}</span>
              <input
                type="datetime-local"
                value={filters.date_from}
                onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setCurrentPage(1); }}
                style={refundsDateInputStyle}
              />
            </div>
            <div style={refundsDateColStyle}>
              <span style={refundsFilterLabelStyle}>{t('common.to') || 'To'}</span>
              <input
                type="datetime-local"
                value={filters.date_to}
                onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setCurrentPage(1); }}
                style={refundsDateInputStyle}
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  alignSelf: 'flex-end',
                  height: 34, padding: '0 10px',
                  background: 'transparent', border: `1px solid ${tk.border}`,
                  borderRadius: 8, color: tk.textMuted, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12,
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <X size={12} /> {t('common.clear')}
              </button>
            )}
          </>
        }
      />

      {error && (
        <div style={{
          background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          fontSize: 13, color: tk.dangerText,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[0.8, 1.5, 1.5, 1.5, 1, 0.4]} />
      ) : (
        <DataTable
          tk={tk}
          columns={cols}
          rows={currentRefunds}
          renderCell={renderCell}
          emptyMessage={t('refunds.noRefunds') || 'No refunds found'}
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* ── Refund Detail Modal ────────────────────────────────────────────── */}
      {selectedRefund && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            {/* Header */}
            <div style={{
              padding: '18px 22px', borderBottom: `1px solid ${tk.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: tk.text, marginBottom: 4 }}>
                  {t('refunds.refundDetails')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontFamily: '"JetBrains Mono", monospace', fontSize: 13,
                    fontWeight: 700, color: tk.accentText,
                  }}>
                    {selectedRefund.receipt_num || `#${selectedRefund.id}`}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedRefund.receipt_num || '');
                      setReceiptCopied(true);
                      setTimeout(() => setReceiptCopied(false), 1500);
                    }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: tk.textMuted, display: 'flex', alignItems: 'center', padding: 4,
                    }}
                    title={t('common.copy')}
                  >
                    <Copy size={13} />
                  </button>
                  {receiptCopied && (
                    <span style={{ fontSize: 11, color: tk.successText, fontWeight: 600 }}>
                      {t('common.copied')}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <StatusPill tk={tk} kind={getRefundStatusKind(selectedRefund.order_status)}>
                  {getRefundStatusLabel(selectedRefund.order_status)}
                </StatusPill>
                <button
                  onClick={() => setSelectedRefund(null)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: 6,
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    color: tk.textSecondary, cursor: 'pointer',
                  }}
                  aria-label={t('common.close')}
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', padding: '18px 22px', flex: 1 }}>

              {/* Meta grid */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                gap: 12, marginBottom: 16,
              }}>
                {[
                  { label: t('common.date'), value: formatDateTime(selectedRefund.created_at) },
                  { label: t('refunds.processedBy'), value: selectedRefund.refund_cashier_name || selectedRefund.cashier_name || '—' },
                  { label: t('refunds.orderCashier'), value: selectedRefund.order_cashier_name || selectedRefund.cashier_name || '—' },
                  {
                    label: t('refunds.approvedBy'),
                    value: selectedRefund.approver_name
                      ? `${selectedRefund.approver_name}${selectedRefund.approver_role ? ` (${selectedRefund.approver_role})` : ''}`
                      : '—',
                  },
                  {
                    label: t('common.total'),
                    value: null,
                    mono: true,
                    amount: selectedRefund.order_total,
                  },
                  ...(parseFloat(selectedRefund.order_discount_percentage || 0) > 0
                    ? [{ label: t('pos.discount'), value: `${selectedRefund.order_discount_percentage}%`, accent: true }]
                    : []),
                ].map((item, i) => (
                  <div key={i} style={{
                    background: tk.sunken, border: `1px solid ${tk.border}`,
                    borderRadius: 8, padding: '10px 12px',
                  }}>
                    <div style={{
                      fontSize: 10.5, color: tk.textMuted, marginBottom: 4,
                      fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>
                      {item.label}
                    </div>
                    {item.mono ? (
                      <MonoMoney amount={item.amount} tk={tk} size={13} weight={600} />
                    ) : (
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        color: item.accent ? tk.warning : tk.text,
                      }}>
                        {item.value}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Refund amount highlight card */}
              <div style={{
                background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
                borderRadius: 10, padding: '14px 16px', marginBottom: 16,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <RotateCcw size={16} color={tk.dangerText} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: tk.dangerText }}>
                    {t('refunds.refundAmount')}
                  </span>
                </div>
                <MonoMoney
                  amount={selectedRefund.amount}
                  tk={tk}
                  size={18}
                  weight={700}
                  color={tk.dangerText}
                />
              </div>

              {/* Reason */}
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 12, fontWeight: 700, color: tk.textMuted,
                  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8,
                }}>
                  {t('refunds.refundReason')}
                </div>
                <div style={{
                  background: tk.sunken, border: `1px solid ${tk.border}`,
                  borderRadius: 8, padding: '12px 14px',
                  fontSize: 13, color: tk.text, lineHeight: 1.5,
                }}>
                  {selectedRefund.reason || '—'}
                </div>
              </div>

              {/* Refunded items */}
              {selectedRefund.items && selectedRefund.items.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: tk.textMuted,
                    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8,
                  }}>
                    {t('refunds.refundedItems')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {selectedRefund.items.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: tk.sunken, border: `1px solid ${tk.border}`,
                        borderRadius: 8, padding: '10px 14px',
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: tk.text, marginBottom: 2 }}>
                            {item.product_name || t('inventory.uncategorized')}
                          </div>
                          <div style={{ fontSize: 11.5, color: tk.textSecondary }}>
                            {parseFloat(item.refunded_quantity)} &times;{' '}
                            <span style={{ fontFamily: '"JetBrains Mono",monospace' }}>
                              EGP {formatCurrency(item.refunded_quantity > 0 ? item.refunded_amount / item.refunded_quantity : 0)}
                            </span>
                          </div>
                          {item.sku && (
                            <div style={{ fontSize: 10.5, color: tk.textMuted, marginTop: 2 }}>
                              {t('inventory.sku')}: {item.sku}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <MonoMoney
                            amount={item.refunded_amount}
                            tk={tk}
                            size={13}
                            weight={700}
                            color={tk.dangerText}
                          />
                          {item.restock && (
                            <div style={{ fontSize: 10.5, color: tk.successText, marginTop: 4 }}>
                              {t('refunds.restock')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '14px 22px', borderTop: `1px solid ${tk.border}`,
              display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
            }}>
              <button
                onClick={() => handlePrintRefund(selectedRefund)}
                disabled={printingRefundId === selectedRefund.id}
                style={{
                  ...cancelBtnStyle,
                  color: tk.text,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: printingRefundId === selectedRefund.id ? 0.6 : 1,
                  cursor: printingRefundId === selectedRefund.id ? 'not-allowed' : 'pointer',
                }}
              >
                {printingRefundId === selectedRefund.id ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
                {t('common.print')}
              </button>
              <button
                onClick={() => setSelectedRefund(null)}
                style={cancelBtnStyle}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Refunds;
